<template>
  <div id="UpdateProfile" class="user-page">
    <PageHeader :title="$t('profile.account_settings')" icon="icon-feather-settings" />
    <div uk-grid>
      <div class="uk-width-1-4@m uk-flex-last@m">
        <div class="uk-card-default rounded text-center p-4">
          <div class="uk-position-relative my-1">
            <div class="user-profile-photo  m-auto">
              <img id="profile-image-holder" :src="profilePreviewUrl || profileUser.photo_link" alt="">
            </div>
            <div class="uk-position-center">
              <file-upload :multiple="false"
                           :show-preview="false"
                           accept="image/*"
                           icon-class="uil-pen"
                           title=""
                           @input="onProfileFilesChange"
              />
            </div>
          </div>
          <button :disabled="isPhotoSubmitting" class="btn btn-default m-auto mb-3" @click="updateProfilePhoto">
            <span v-if="isPhotoSubmitting" class="mr-2" uk-spinner="ratio: 0.6"></span>
            <i class="uil-image-upload"></i>
            {{ isPhotoSubmitting ? $t('general.saving') : $t('profile.update_photo') }}
          </button>
        </div>
      </div>
      <div class="uk-width-expand@m">
        <div class="uk-card-default rounded profile-form-card">
          <div class="p-3">
            <h5 class="mb-0"> {{ $t('profile.update_personal_information') }} </h5>
            <div v-if="successMessage" class="uk-alert-success" uk-alert>
              <a class="uk-alert-close" uk-close></a>
              <p style="color:green;">
                <i class="uil-exclamation-triangle"></i> {{ successMessage }}
              </p>
            </div>
            <div v-if="errors" class="uk-alert-danger" uk-alert>
              <a class="uk-alert-close" uk-close></a>
              <p style="color:red;">
                <i class="uil-exclamation-triangle"></i> {{ errors }}
              </p>
            </div>
            <div v-for="error in errorMessages" v-if="errorMessages" class="uk-alert-danger" uk-alert>
              <a class="uk-alert-close" uk-close></a>
              <p style="color:red;">
                <i class="uil-exclamation-triangle"></i> {{ error }}
              </p>
            </div>
          </div>
          <hr class="m-0">
          <form autocomplete="off" @submit.prevent="onSubmit()">
            <div class="uk-child-width-1-2@s uk-grid-small p-4" uk-grid>
              <div>
                <SfInput v-model="profileUser.name" :disabled="true" :label="$t('profile.name')" input-class="uk-input"
                         name="profile_name" type="text"
                />
              </div>
              <div>
                <SfInput v-model="profileUser.surname" :disabled="true" :label="$t('profile.surname')"
                         input-class="uk-input" name="profile_surname" type="text"
                />
              </div>
              <div>
                <SfInput v-model="profileUser.dob" :label="$t('profile.dob')" input-class="uk-input" name="profile_dob"
                         type="date"
                />
              </div>
              <div v-if="!shouldHideEmailField">
                <SfInput v-model="profileUser.email" :disabled="true" :label="$t('profile.email')"
                         input-class="uk-input" name="profile_email" type="text"
                />
              </div>
              <div>
                <h5 class="uk-text-bold mb-2"> {{ $t('profile.phone') }} </h5>
                <PhoneInput v-model="maskPhone" :country-phone-code="profileUser?.country_phone_code"
                            name="profile_phone"/>
              </div>
            </div>
            <hr>

            <password-validation :password="password" :passwordConfirmation="password_confirmation"
                                 class="uk-child-width-1@s"
                                 @update:password="password=$event"
                                 @update:passwordConfirmation="password_confirmation=$event"
            />

            <div class="uk-child-width-1-2@s uk-grid-small p-4" uk-grid>

              <div v-if="company.is_allowed_login_type" style="position: relative; z-index: 2;">
                <h5 class="uk-text-bold mb-2">{{ $t('profile.login_type') }}</h5>
                <multiple-select-input v-model="selectedLoginType" :allow-empty="true" :searchable="false"
                                       :show-labels="false"
                                       :values="login_types" label="name" name="login_type" track-by="id"
                />
              </div>
              <div v-if="company.is_allowed_authentication_type" style="position: relative; z-index: 1;">
                <h5 class="uk-text-bold mb-2">{{ $t('profile.authentication_type') }}</h5>
                <multiple-select-input v-model="selectedAuthenticationType" :allow-empty="true" :searchable="false"
                                       :show-labels="false"
                                       :values="translatedAuthenticationTypes" label="name" name="authentication_type"
                                       track-by="id"
                />
              </div>
            </div>
          </form>
          <div class="uk-flex uk-flex-right p-4">
            <button class="btn btn-link mr-2" @click="$router.go(-1)">{{ $t('general.cancel') }}</button>
            <button :disabled="isSubmitting" class="btn btn-default grey" @click="onSubmit()">
              <span v-if="isSubmitting" class="mr-2" uk-spinner="ratio: 0.6"></span>
              <span>{{ $t('general.save') }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import module, {
  CURRENT_ITEM,
  ERROR,
  GET_USER,
  SET_ERROR,
  UPDATE_USER,
  UPDATE_USER_PHOTO,
  MODULE_NAME
} from "@/core/services/store/user.module";
import moment from "moment";
import store from "@/core/services";
import { TheMask } from 'vue-the-mask';
import SecureLS from "secure-ls";
import axios from "axios";
import PasswordValidation from "@/view/components/validation/PasswordValidation.vue";
import SfInput from "@/view/components/ui/SfInput.vue";
import MultipleSelectInput from "@/assets/components/inputs/MultipleSelectInput";
import PhoneInput from "@/assets/components/inputs/PhoneInput";
import FileUpload from "@/view/components/input/fileUpload/fileUpload.vue";
import PageHeader from "@/view/components/common/PageHeader.vue";
import { showError, showSuccess } from "@/utils/alertUtils";

const ls = new SecureLS({ encodingType: 'aes', isCompression: true });

export default {
  name: "UpdateProfile",
  components: {
    TheMask,
    PasswordValidation,
    SfInput,
    MultipleSelectInput,
    PhoneInput,
    FileUpload,
    PageHeader
  },
  data() {
    return {
      maskPhone: '',
      apiURL: 'api/user/',
      isSubmitting: false,
      isPhotoSubmitting: false,
      successMessage: '',
      password: null,
      password_confirmation: null,
      user_photo: null,
      profileImage: null,
      profilePreviewUrl: null,
      errorMessages: [],
      errorMessage: null,
      company: {},

      login_types: [],
      authentication_types: [],
      profileUserCopy: null
    };
  },
  computed: {
    errors: {
      get() {
        return store.getters[MODULE_NAME + '/' + ERROR]
      },
      set(value) {
      }
    },
    profileUser: {
      get() {
        let user = store.getters[MODULE_NAME + '/' + CURRENT_ITEM]
        user.dob = user.dob ? moment(user.dob).format('YYYY-MM-DD') : null

        if (user.phone && user.phone.length === 10) {
          user.phone = "0" + user.phone;
        }

        if (user.phone && user.country_phone_code) {
          let phoneNumber = user.phone.replace(/^0/, "");
          this.maskPhone = `+${user.country_phone_code} ${phoneNumber}`;
        }

        // profileUserCopy sadece 1 kez tetiklensin
        if (!this.profileUserCopy && user && user.id) {
          this.profileUserCopy = JSON.parse(JSON.stringify(user));
        }

        return user
      },
      set(value) {
      }
    },
    selectedLoginType: {
      get() {
        return this.login_types.find((item) => item.id === this.profileUser?.login_type_id) || null
      },
      set(value) {
        this.profileUser.login_type_id = value ? value.id : null
      }
    },
    translatedAuthenticationTypes() {
      return this.authentication_types.map(item => ({
        id: item.id,
        name: this.$t('general.' + item.key_name)
      }));
    },
    shouldHideEmailField() {
      const companyId = this.profileUser?.company_id || this.company?.id;
      return Number(companyId) === 489;
    },
    selectedAuthenticationType: {
      get() {
        return this.translatedAuthenticationTypes.find((item) => item.id === this.profileUser?.authentication_type_id) || null
      },
      set(value) {
        this.profileUser.authentication_type_id = value ? value.id : null
      }
    }
  },
  methods: {
    getAuthenticationTypes() {
      axios.get(`api/user/authentication-types`, {
        headers: this.apiHeaders
      }).then((result) => {
        this.authentication_types = result.data.map(data => ({
          id: data.id,
          key_name: data.key_name
        }));
      })
    },
    getLoginTypes() {
      let types = []
      axios.get(`api/user/login-types`, {
        headers: this.apiHeaders
      }).then((result) => {
        result.data.forEach(data => {
          types.push({
            id: data.id,
            name: this.$t('general.' + data.key_name)
          })
        });
      })
      this.login_types = types
    },
    getCompany() {
      let self = this;
      axios.get(`api/company/self`, {
        headers: this.apiHeaders
      }).then((result) => {
        self.company = result.data
      })
    },
    onProfileFilesChange(files) {
      const selectedFile = Array.isArray(files) && files.length > 0 ? files[0] : null;
      this.profileImage = selectedFile;
      if (this.profilePreviewUrl) {
        URL.revokeObjectURL(this.profilePreviewUrl);
      }
      this.profilePreviewUrl = selectedFile ? URL.createObjectURL(selectedFile) : null;
    },
    format(value) {
      return moment(value).format('YYYY-MM-DD')
    },
    onSubmit() {
      if (this.isSubmitting) return;
      this.isSubmitting = true;
      this.$store.commit(MODULE_NAME + '/' + SET_ERROR, null)

      // let phoneNumber = this.profileUser.phone;
      let country_phone_code = this.maskPhone.substring(0, this.maskPhone.indexOf(' ')).substring(1)
      this.maskPhone = this.maskPhone.substring(this.maskPhone.indexOf(' ') + 1).replaceAll(' ', '');
      // phoneNumber = phoneNumber.replace(/ /g, '').substring(1);
      let form = {
        dob: this.profileUser.dob,
        phone: country_phone_code ? this.maskPhone : null,
        login_type_id: this.profileUser.login_type_id,
        authentication_type_id: this.profileUser.authentication_type_id,
        country_phone_code: this.maskPhone ? country_phone_code : null,
        // DOM müdahalesine karşı orjinali profileUserCopy de tuttuk
        name: this.profileUserCopy.name,
        surname: this.profileUserCopy.surname,
      }
      this.errorMessages = [];
      this.successMessage = null;

      if (!this.validateProfileForm()) {
        this.isSubmitting = false;
        return false;
      }

      if (this.password && this.password_confirmation) {
        form.password = this.password;
        form.password_confirmation = this.password_confirmation;
      }

      this.$store.dispatch(MODULE_NAME + '/' + UPDATE_USER, {
        url: this.apiURL + this.profileUser.id,
        contents: form,
      }).then(() => {
        this.successMessage = this.$t('profile.your_information_updated')
        showSuccess(this.$t('profile.your_information_updated'))
        let user_data = this.authUser
        user_data.fullname = form.name + ' ' + form.surname
        user_data.name = form.name
        user_data.surname = form.surname

        window.localStorage.removeItem('user');
        let tempUser = {};
        Object.assign(tempUser, user_data);
        ls.set('user', JSON.stringify(tempUser));

        setTimeout(() => {
          location.reload()
        }, 1000)

      }).catch((error) => {
        const responseData = error?.response?.data;
        showError(responseData?.message || error)
      }).finally(() => {
        this.isSubmitting = false;
      })
    },

    validateProfileForm() {
      this.errorMessages = [];

      if (!this.profileUser?.name || this.profileUser.name.length === 0) {
        this.errorMessages.push(this.$t('errors.user_name_required'));
      }

      if (!this.profileUser?.surname || this.profileUser.surname.length === 0) {
        this.errorMessages.push(this.$t('errors.user_surname_required'));
      }

      if (!this.shouldHideEmailField && (!this.profileUser?.email || this.profileUser.email.length === 0)) {
        this.errorMessages.push(this.$t('errors.user_email_required'));
      }

      if (this.password) {
        const lowerCaseLetters = /[a-z]/g;
        const upperCaseLetters = /[A-Z]/g;
        const numbers = /[0-9]/g;

        if (!this.password.match(lowerCaseLetters)) {
          this.errorMessages.push(this.$t('errors.password_must_have_lower_latter'));
        }

        if (!this.password.match(upperCaseLetters)) {
          this.errorMessages.push(this.$t('errors.password_must_have_upper_latter'));
        }

        if (!this.password.match(numbers)) {
          this.errorMessages.push(this.$t('errors.password_must_have_number'));
        }

        if (this.password.length < 7) {
          this.errorMessages.push(this.$t('errors.password_must_more_than_7'));
        }
      }

      return this.errorMessages.length === 0;
    },
    updateProfilePhoto() {
      if (this.isPhotoSubmitting) return;
      this.isPhotoSubmitting = true;
      this.$store.commit(MODULE_NAME + '/' + SET_ERROR, null)
      let form = new FormData();
      form.append('photo', this.profileImage);
      this.$store.dispatch(MODULE_NAME + '/' + UPDATE_USER_PHOTO, {
        url: this.apiURL + this.profileUser.id + '/photo',
        contents: form,
      }).then((response) => {
        if (!this.errors) {
          this.successMessage = this.$t('profile.your_information_updated')
          let user_data = this.authUser
          if (this.profileImage) {
            user_data.photo = this.profileUser.photo_link
          }
          window.localStorage.removeItem('user');
          let tempUser = {};
          Object.assign(tempUser, user_data);
          ls.set('user', JSON.stringify(tempUser));
          location.reload()
        }
      }).finally(() => {
        this.isPhotoSubmitting = false;
      })
    }
  },
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule)
      }
    }

    registerStoreModule(MODULE_NAME, module)
  },
  mounted() {
    this.$store.dispatch(MODULE_NAME + '/' + GET_USER, {
      url: this.apiURL + 'self',
      acceptPromise: true,
    })
    this.getAuthenticationTypes()
    this.getLoginTypes()
    this.getCompany()
  },
}
</script>

<style scoped>
#UpdateProfile .uk-card-default {
  background: #ffffff;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  box-shadow: none !important;
  overflow: hidden;
}

#UpdateProfile input,
#UpdateProfile select,
#UpdateProfile textarea,
#UpdateProfile .uk-input,
#UpdateProfile .uk-select {
  border: 1px solid #d7dce3;
  border-radius: 8px;
  background-color: #fff;
  color: #1f2937;
  box-shadow: none;
}

#UpdateProfile .vue-tel-input {
  border: 1px solid #d7dce3;
  border-radius: 8px;
  background-color: #fff;
  color: #1f2937;
  box-shadow: none;
  display: flex;
  align-items: center;
  overflow: hidden;
  padding: 0;
  height: 42px;
  position: relative;
  z-index: 1;
}

#UpdateProfile .vue-tel-input .vti__input {
  border: 0;
  padding: 10px 12px;
  height: 42px;
  background-color: transparent;
  box-shadow: none;
}

#UpdateProfile select,
#UpdateProfile .uk-select {
  height: 42px;
}

#UpdateProfile .uk-input,
#UpdateProfile select,
#UpdateProfile textarea,
#UpdateProfile .vue-tel-input,
#UpdateProfile .vue-tel-input .vti__input,
#UpdateProfile .vue-tel-input .vti__dropdown {
  pointer-events: auto;
}

#UpdateProfile .uk-input,
#UpdateProfile select,
#UpdateProfile textarea {
  position: relative;
  z-index: 1;
}

#UpdateProfile .vue-tel-input .vti__dropdown {
  border: 0;
  height: 42px;
  background-color: transparent;
  box-shadow: none;
}

#UpdateProfile .custom-multiselect .multiselect__tags {
  border: 1px solid #d7dce3;
  border-radius: 8px;
  background-color: #fff;
  min-height: 42px;
  padding: 6px 12px;
  box-shadow: none;
}

#UpdateProfile .custom-multiselect .multiselect__single,
#UpdateProfile .custom-multiselect .multiselect__input {
  background-color: transparent;
}

#UpdateProfile .custom-multiselect .multiselect__select {
  height: 42px;
}

#UpdateProfile :deep(.vue-tel-input) {
  border: 1px solid #d7dce3 !important;
  border-radius: 8px !important;
  background-color: #fff !important;
  color: #1f2937 !important;
  box-shadow: none !important;
  display: flex;
  align-items: center;
  padding: 0;
  height: 42px;
}

#UpdateProfile :deep(.vue-tel-input .vti__input) {
  border: 0 !important;
  padding: 10px 12px !important;
  height: 42px !important;
  background-color: transparent !important;
  box-shadow: none !important;
  font-size: 14px !important;
  line-height: 1.4 !important;
  color: #1f2937 !important;
}

#UpdateProfile :deep(.vue-tel-input .vti__dropdown) {
  border: 0 !important;
  height: 42px !important;
  background-color: transparent !important;
  box-shadow: none !important;
  color: #1f2937 !important;
}

#UpdateProfile .profile-form-card {
  overflow: visible !important;
}

#UpdateProfile :deep(.vue-tel-input .vti__dropdown-list) {
  z-index: 1000;
}

#UpdateProfile .uk-card-default,
#UpdateProfile form,
#UpdateProfile .uk-grid,
#UpdateProfile .uk-grid > div {
  position: relative;
  z-index: 2;
}

#UpdateProfile .password-validation {
  z-index: 1;
}

#UpdateProfile .sf-input-wrapper,
#UpdateProfile .sf-input-wrapper input,
#UpdateProfile .custom-multiselect,
#UpdateProfile .custom-multiselect * {
  pointer-events: auto;
  position: relative;
  z-index: 3;
}

#UpdateProfile {
  pointer-events: auto;
}

@media (max-width: 640px) {
  #UpdateProfile .uk-flex.uk-flex-right {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }

  #UpdateProfile .uk-flex.uk-flex-right .btn {
    width: 100%;
  }
}
</style>
